import React, { forwardRef, HTMLAttributes } from "react";
import styled from "styled-components";
import Close from "../icons/Close";
import ErrorMessage from "./ErrorMessage";
import { Breakpoint } from "../enums/Breakpoints";
import Button from "./TixUiButton";

interface Props extends HTMLAttributes<HTMLInputElement> {
  autoFocus?: boolean;
  disabled?: boolean;
  error?: string;
  hasError?: boolean;
  icon?: JSX.Element; // TODO: need to be improved when icon wrapper is provided
  id?: string;
  label?: string;
  name: string;
  onClear?(): void;
  placeholder?: string | undefined;
  required?: boolean;
  type?: "email" | "password" | "text";
  value: string;
  readOnly?: boolean;
}

const TextField = forwardRef<HTMLInputElement, Props>(
  (
    {
      autoFocus,
      disabled,
      error,
      hasError,
      icon,
      id,
      label,
      name,
      onChange,
      onClear,
      onFocus,
      onMouseDown,
      placeholder,
      required,
      type = "text",
      value,
      readOnly,
    },
    ref
  ) => {
    return (
      <FieldContainer hasError={hasError || !!error} hasIcon={!!icon}>
        {icon}
        <input
          aria-label={label}
          autoFocus={autoFocus}
          {...(autoFocus && { "data-autofocus": true })}
          disabled={disabled}
          id={id || name}
          name={name}
          type={type}
          placeholder={placeholder || label}
          onFocus={onFocus}
          onChange={onChange}
          onMouseDown={onMouseDown}
          required={required}
          value={value}
          autoCorrect="off"
          autoComplete="off"
          ref={ref}
          readOnly={readOnly}
        />
        {value && onClear && (
          <ClearButton color="transparent" onClick={onClear} onlyIcon>
            <Close />
          </ClearButton>
        )}
        {error && <ErrorMessage error={error} />}
      </FieldContainer>
    );
  }
);

const FieldContainer = styled.div<{ hasError: boolean; hasIcon: boolean }>`
  align-items: flex-start;
  font-family: ${({ theme }) => theme.redesign.regularFont};
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  & > input {
    background: ${({ theme }) => theme.redesign.defaultBackground};
    color: ${({ hasError, theme }) =>
      hasError ? theme.redesign.errorColor : theme.redesign.defaultColor};
    padding: ${({ hasIcon }) => (hasIcon ? "0 20px 0 47px" : "0 20px")};
    font-size: 14px;
    font-family: inherit;
    border-radius: ${({ theme }) => theme.redesign.ovalBorderRadius};
    border: none;
    height: 44px;
    box-sizing: border-box;
    text-overflow: ellipsis;
    width: 100%;
    outline: 0;

    &::placeholder {
      color: ${({ hasError, theme }) =>
        hasError
          ? theme.redesign.errorColor
          : theme.redesign.form.textField.placeholderColor};
      opacity: 1;
    }

    @media ${Breakpoint.MinDesktop} {
      height: 48px;
      padding: ${({ hasIcon }) => (hasIcon ? "0 20px 0 50px" : "0 20px")};
    }

    /* TODO: I can't find any example of this */
    /* &:disabled {
      background: #f4f4f4;
      cursor: unset;
    } */
  }

  & > .error-message {
    margin: 10px 0 0 20px;
  }

  /* TODO: replace it when Custom icon wrapper is ready */
  & > svg {
    position: absolute;
    top: 24px;
    transform: translateY(-50%);
    left: 20px;
  }
`;

const ClearButton = styled(Button)`
  padding: 0;
  height: 14px;
  width: 14px;
  position: absolute;
  top: 15px;
  right: 20px;
`;

export default TextField;
